import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { ContentWrap } from '../components/ContentWrap.js';
import { Button } from '../components/Buttons/Button.js';
import { Heading, PageContent } from '../components/index.js';
import { getMessage } from '../i18n/messages.js';
import { classifyAxiosError } from '@digiwedge/hooks-auth-web';
import { useOptionalIdp } from '../providers/IdpProvider.js';
import { toast } from '../utils/toast.js';
import { IdpRequestError, postIdpJson } from '../utils/idp-http.js';
const FORGOT_PASSWORD_BG_IMAGE = 'https://images.pexels.com/photos/114972/pexels-photo-114972.jpeg';
function isValidEmail(value) {
    return /.+@.+\..+/.test(value.trim());
}
function buildLoginHref(returnTo) {
    if (!returnTo) {
        return '/login';
    }
    const params = new URLSearchParams();
    params.set('returnTo', returnTo);
    return `/login?${params.toString()}`;
}
export function ForgotPasswordPage() {
    const location = useLocation();
    const initialEmail = useMemo(() => {
        try {
            const params = new URLSearchParams(location.search);
            return params.get('email') ?? '';
        }
        catch {
            return '';
        }
    }, [location.search]);
    const returnTo = useMemo(() => {
        try {
            const params = new URLSearchParams(location.search);
            const candidate = params.get('returnTo');
            return candidate && candidate.startsWith('/') ? candidate : null;
        }
        catch {
            return null;
        }
    }, [location.search]);
    const loginHref = useMemo(() => buildLoginHref(returnTo), [returnTo]);
    const [email, setEmail] = useState(initialEmail);
    const [touched, setTouched] = useState(Boolean(initialEmail));
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const idp = useOptionalIdp();
    const requestPasswordReset = useMemo(() => {
        if (idp?.requestPasswordReset) {
            return idp.requestPasswordReset;
        }
        return (value) => postIdpJson('/api/auth/forgot-password', { email: value });
    }, [idp]);
    const onSubmit = async (event) => {
        event.preventDefault();
        setTouched(true);
        const trimmedEmail = email.trim();
        if (!isValidEmail(trimmedEmail)) {
            setError(getMessage('en-US', 'forgotPasswordInvalidEmail'));
            return;
        }
        setError(null);
        setLoading(true);
        try {
            await requestPasswordReset(trimmedEmail);
            setSubmitted(true);
            toast.success(getMessage('en-US', 'forgotPasswordSuccess'));
        }
        catch (err) {
            const fallback = getMessage('en-US', 'genericError');
            if (isAxiosError(err)) {
                const result = classifyAxiosError(err, fallback);
                setError(result.message);
                toast.error(result.message);
            }
            else if (err instanceof IdpRequestError) {
                const msg = err.message || fallback;
                setError(msg);
                toast.error(msg);
            }
            else {
                setError(fallback);
                toast.error(fallback);
            }
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsxs(PageContent, { className: "relative min-h-screen overflow-hidden bg-gray-50", children: [_jsx("div", { className: "absolute inset-0 bg-cover bg-center", style: { backgroundImage: `url(${FORGOT_PASSWORD_BG_IMAGE})` }, "aria-hidden": "true" }), _jsx("div", { className: "absolute inset-0 bg-white/55 backdrop-blur-sm", "aria-hidden": "true" }), _jsx(ContentWrap, { className: "relative flex justify-center py-12 items-center min-h-screen", children: _jsxs("div", { className: "w-full max-w-md bg-white/90 backdrop-blur px-6 py-10 rounded-lg shadow flex flex-col gap-6", children: [_jsxs("div", { className: "text-center flex flex-col gap-3", children: [_jsx(Heading, { variant: "h4", children: getMessage('en-US', 'forgotPasswordHeading') }), !submitted && (_jsx("p", { className: "text-sm text-gray-600", children: getMessage('en-US', 'forgotPasswordDescription') }))] }), submitted ? (_jsxs("div", { className: "flex flex-col gap-3 text-center", children: [_jsx("p", { className: "text-sm text-gray-700", children: getMessage('en-US', 'forgotPasswordSuccess') }), _jsx(Link, { to: loginHref, className: "text-primary hover:underline text-sm", children: getMessage('en-US', 'goToLogin') })] })) : (_jsxs("form", { onSubmit: onSubmit, className: "flex flex-col gap-4", noValidate: true, children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "email", className: "block text-sm font-medium mb-1", children: getMessage('en-US', 'forgotPasswordEmailLabel') }), _jsx("input", { id: "email", type: "email", autoComplete: "email", required: true, value: email, onChange: (e) => setEmail(e.target.value), onBlur: () => setTouched(true), className: "w-full h-11 px-3 border border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent", placeholder: getMessage('en-US', 'forgotPasswordEmailPlaceholder'), "aria-invalid": touched && !isValidEmail(email) }), touched && !isValidEmail(email) && (_jsx("p", { className: "mt-1 text-xs text-red-600", children: getMessage('en-US', 'forgotPasswordInvalidEmail') }))] }), error && (_jsx("div", { className: "bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded", role: "alert", children: error })), _jsx(Button, { htmlType: "submit", disabled: loading, "aria-busy": loading, className: "w-full bg-primary text-white py-2 rounded-md hover:bg-primary/80 disabled:opacity-60", children: loading ? (_jsxs("span", { className: "flex items-center justify-center gap-2", children: [_jsx("span", { className: "h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent", "aria-hidden": "true" }), getMessage('en-US', 'forgotPasswordSubmit')] })) : (getMessage('en-US', 'forgotPasswordSubmit')) }), _jsxs("div", { className: "text-center text-sm text-gray-700", children: [_jsx("span", { children: getMessage('en-US', 'forgotPasswordRemembered') }), ' ', _jsx(Link, { to: loginHref, className: "text-primary hover:underline", children: getMessage('en-US', 'goToLogin') })] })] }))] }) })] }));
}
//# sourceMappingURL=ForgotPasswordPage.js.map